import { useEffect, useLayoutEffect } from 'react';

/**
 * Ports the vanilla `landing.js` from the handoff into a single React effect.
 * Owns: hero mouse-follow spotlight, sync-line SVG (hero coach↔athlete),
 * set-ticking animations in hero + athlete-big window, pricing/athlete tab
 * cycle. The FAQ accordion is React-state-driven inside the FAQ component, so
 * it's NOT touched here.
 *
 * All listeners/intervals are cleaned up on unmount and paused when the tab is
 * hidden (visibility API) so the page doesn't eat CPU in the background.
 */
export function useLandingAnimations() {
  // Hero spotlight — pointermove → CSS vars on the hero element.
  useEffect(() => {
    const hero = document.querySelector<HTMLElement>('.et-landing .hero');
    if (!hero) return;
    let raf: number | null = null;
    const onMove = (e: PointerEvent) => {
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        hero.style.setProperty('--spot-x', x + '%');
        hero.style.setProperty('--spot-y', y + '%');
        raf = null;
      });
    };
    hero.addEventListener('pointermove', onMove);
    return () => {
      hero.removeEventListener('pointermove', onMove);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  // Sync line SVG between coach and athlete windows. Runs after layout so we
  // can measure both windows.
  useLayoutEffect(() => {
    const stage = document.getElementById('stage');
    if (!stage) return;
    const coachEl = stage.querySelector<HTMLElement>('.window.coach');
    const athEl = stage.querySelector<HTMLElement>('.window.athlete');
    const svg = stage.querySelector<SVGSVGElement>('.sync-svg');
    const path = document.getElementById('syncPath') as unknown as SVGPathElement | null;
    const badge = document.getElementById('syncBadge') as HTMLElement | null;
    if (!coachEl || !athEl || !svg || !path) return;

    const layout = () => {
      const sr = stage.getBoundingClientRect();
      const cr = coachEl.getBoundingClientRect();
      const ar = athEl.getBoundingClientRect();
      svg.setAttribute('width', String(sr.width));
      svg.setAttribute('height', String(sr.height));
      svg.setAttribute('viewBox', `0 0 ${sr.width} ${sr.height}`);

      const sx = cr.right - sr.left - 4;
      const sy = cr.top + cr.height * 0.55 - sr.top;
      const ex = ar.left - sr.left + 4;
      const ey = ar.top + ar.height * 0.45 - sr.top;
      const cx1 = sx + (ex - sx) * 0.5;
      const cy1 = sy;
      const cx2 = sx + (ex - sx) * 0.5;
      const cy2 = ey;
      path.setAttribute('d', `M ${sx} ${sy} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${ex} ${ey}`);

      if (badge) {
        const mx = (sx + ex) / 2;
        const my = (sy + ey) / 2;
        badge.style.left = mx - 64 + 'px';
        badge.style.top = my - 24 + 'px';
      }
    };
    const onLoad = () => layout();
    window.addEventListener('load', onLoad);
    window.addEventListener('resize', layout);
    const t1 = setTimeout(layout, 100);
    const t2 = setTimeout(layout, 600);
    layout();
    return () => {
      window.removeEventListener('load', onLoad);
      window.removeEventListener('resize', layout);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Hero athlete set-ticking (every 2.4s the active set turns done, reset at end).
  useEffect(() => {
    const setList = document.getElementById('setList');
    const counter = document.getElementById('setCounter');
    if (!setList || !counter) return;
    let cursor = 2;
    let interval: ReturnType<typeof setInterval> | null = null;
    let resetTimeout: ReturnType<typeof setTimeout> | null = null;

    const step = () => {
      const items = Array.from(setList.querySelectorAll<HTMLElement>('.set-item'));
      if (!items.length) return;
      const cur = items[cursor];
      if (!cur) return;
      cur.classList.remove('active');
      cur.classList.add('done');
      const lbl = cur.querySelector<HTMLElement>('.set-label');
      const vals = cur.querySelector<HTMLElement>('.set-vals');
      if (lbl) lbl.textContent = `Set ${cursor + 1}`;
      if (vals) vals.textContent = '6 × 140kg';
      cursor++;
      if (cursor >= items.length) {
        resetTimeout = setTimeout(() => {
          items.forEach((el, i) => {
            const l = el.querySelector<HTMLElement>('.set-label');
            const v = el.querySelector<HTMLElement>('.set-vals');
            if (i < 2) {
              el.className = 'set-item done';
              if (l) l.textContent = `Set ${i + 1}`;
              if (v) v.textContent = '6 × 140kg';
            } else if (i === 2) {
              el.className = 'set-item active';
              if (l) l.textContent = 'Set 3 · en curso';
              if (v) v.textContent = '— × 140kg';
            } else {
              el.className = 'set-item';
              if (l) l.textContent = `Set ${i + 1}`;
              if (v) v.textContent = '— × 140kg';
            }
          });
          counter.innerHTML = '3<span class="of"> / 4</span>';
          cursor = 2;
        }, 1800);
      } else {
        const next = items[cursor];
        if (!next) return;
        next.classList.add('active');
        const l2 = next.querySelector<HTMLElement>('.set-label');
        if (l2) l2.textContent = `Set ${cursor + 1} · en curso`;
        counter.innerHTML = `${cursor + 1}<span class="of"> / 4</span>`;
      }
    };

    const start = () => {
      if (interval == null) interval = setInterval(step, 2400);
    };
    const stop = () => {
      if (interval != null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);
    start();

    return () => {
      stop();
      if (resetTimeout) clearTimeout(resetTimeout);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Athlete-big set ticking (the bigger product window in <ForAthletes>).
  useEffect(() => {
    const athBigSets = document.querySelector<HTMLElement>('.ath-big-sets');
    if (!athBigSets) return;
    let cursor = 2;
    let interval: ReturnType<typeof setInterval> | null = null;
    const items = Array.from(athBigSets.querySelectorAll<HTMLElement>('.set'));
    if (!items.length) return;

    const tick = () => {
      if (cursor < items.length) {
        const cur = items[cursor];
        if (!cur) return;
        cur.classList.remove('active');
        cur.classList.add('done');
        const v = cur.querySelector<HTMLElement>('.vals');
        if (v) v.textContent = '6 × 80kg';
        cursor++;
        const next = items[cursor];
        if (cursor < items.length && next) next.classList.add('active');
      } else {
        items.forEach((el, i) => {
          const v = el.querySelector<HTMLElement>('.vals');
          if (i < 2) {
            el.className = 'set done';
            if (v) v.textContent = '6 × 80kg';
          } else if (i === 2) {
            el.className = 'set active';
            if (v) v.textContent = '— × 80kg';
          } else {
            el.className = 'set';
            if (v) v.textContent = '— × 80kg';
          }
        });
        cursor = 2;
      }
    };
    const start = () => {
      if (interval == null) interval = setInterval(tick, 2400);
    };
    const stop = () => {
      if (interval != null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Cosmetic tab cycle inside the athlete-big window.
  useEffect(() => {
    const tabs = document.querySelector<HTMLElement>('.ath-big-tabs');
    if (!tabs) return;
    const btns = Array.from(tabs.querySelectorAll<HTMLButtonElement>('button'));
    const handlers: Array<() => void> = [];
    btns.forEach((b) => {
      const h = () => {
        btns.forEach((x) => x.classList.remove('active'));
        b.classList.add('active');
      };
      b.addEventListener('click', h);
      handlers.push(() => b.removeEventListener('click', h));
    });
    return () => handlers.forEach((fn) => fn());
  }, []);
}
